/**
 * StudioMemory — AI Channel Memory dashboard.
 *
 * Shows the per-channel embedding store: counts by kind, top-performing
 * titles, and a live semantic-search box. One-click "Sync" backfills
 * from existing content.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Activity, Search, RefreshCw, Loader2, Sparkles, ArrowLeft,
  AlertTriangle, Trophy, TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StudioMemory() {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [query, setQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  // Load channels
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/studio/channels`, { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        const all = [...(r.data?.owned || []), ...(r.data?.shared || [])];
        setChannels(all);
        if (!channelId && all[0]) setChannelId(all[0].channel_id);
      })
      .catch((err) => alive && setError(err?.response?.data?.detail || err.message));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load stats when channel changes
  useEffect(() => {
    if (!channelId) return;
    let alive = true;
    setLoading(true);
    axios.get(`${API}/api/studio/memory/stats/${channelId}`, { withCredentials: true })
      .then((r) => { if (alive) setStats(r.data); })
      .catch(() => { if (alive) setStats(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [channelId]);

  const sync = async () => {
    if (!channelId) return;
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/api/studio/memory/sync/${channelId}`, {}, { withCredentials: true });
      toast.success(`Synced ${r.data?.added || 0} memory items`);
      const s = await axios.get(`${API}/api/studio/memory/stats/${channelId}`, { withCredentials: true });
      setStats(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const search = async () => {
    if (!channelId || !query.trim()) return;
    setSearching(true);
    try {
      const params = new URLSearchParams({ channel_id: channelId, q: query.trim(), top_k: "10" });
      if (kindFilter) params.set("kind", kindFilter);
      const r = await axios.get(`${API}/api/studio/memory/search?${params}`, { withCredentials: true });
      setResults(r.data?.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const totalCount = stats?.by_kind
    ? Object.values(stats.by_kind).reduce((s, v) => s + (v.count || 0), 0)
    : 0;

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <Link to="/studio" className="studio-btn-ghost" data-testid="mem-back" style={{ marginBottom: 12 }}>
            <ArrowLeft size={14} /> Back to Studio
          </Link>
          <span className="studio-eyebrow">
            <Activity size={12} /> AI Channel Memory · BGE embeddings
          </span>
          <h1 className="studio-h1">
            Your channel&apos;s greatest hits, <span className="studio-h1-accent">in vector form.</span>
          </h1>
          <p className="studio-sub">
            Every title, hook and description from your past videos lives here as a 384-dim semantic vector.
            The SEO Assistant and Topic Generator pull from this memory to keep new ideas on-brand and on-trend.
          </p>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            <select
              className="studio-input"
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              data-testid="mem-channel-select"
              style={{ minWidth: 240 }}
            >
              <option value="">Pick a channel…</option>
              {channels.map((c) => (
                <option key={c.channel_id} value={c.channel_id}>{c.title}</option>
              ))}
            </select>
            <button
              className="studio-btn-primary"
              onClick={sync}
              disabled={!channelId || syncing}
              data-testid="mem-sync-btn"
            >
              {syncing ? <Loader2 size={14} className="ac-spin" /> : <RefreshCw size={14} />}
              {syncing ? "Indexing…" : "Backfill from content"}
            </button>
          </div>
        </div>

        {error && (
          <div className="ac-error" data-testid="mem-error" style={{ marginBottom: 14 }}>
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {loading && (
          <div className="studio-skeleton studio-sk-card" style={{ height: 100 }} />
        )}

        {!loading && stats && (
          <>
            {/* Stats strip */}
            <div className="studio-ops-row" data-testid="mem-stats">
              <div className="studio-ops-card">
                <header><span className="studio-eyebrow"><Activity size={12} /> Total vectors</span></header>
                <p style={{ fontSize: 26, fontWeight: 800, margin: "6px 0 0" }}>{totalCount}</p>
              </div>
              {["title", "hook", "description", "tags"].map((k) => (
                <div key={k} className="studio-ops-card" data-testid={`mem-stat-${k}`}>
                  <header>
                    <span className="studio-eyebrow" style={{ textTransform: "capitalize" }}>
                      <Sparkles size={12} /> {k}
                    </span>
                  </header>
                  <p style={{ fontSize: 22, fontWeight: 700, margin: "6px 0 0" }}>
                    {stats.by_kind?.[k]?.count || 0}
                  </p>
                  <small className="studio-muted">
                    avg perf {Math.round(stats.by_kind?.[k]?.avg_perf || 0)}
                  </small>
                </div>
              ))}
            </div>

            {/* Top titles */}
            {stats.top_titles?.length > 0 && (
              <div className="studio-card" style={{ padding: 18, marginTop: 14 }} data-testid="mem-top-titles">
                <h3><Trophy size={14} style={{ display: "inline", marginRight: 6, color: "#f59e0b" }} />Top performers</h3>
                <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 8 }}>
                  {stats.top_titles.map((t, i) => (
                    <li key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0" }}>
                      <span className="studio-pill studio-pill--sm" style={{ minWidth: 32, textAlign: "center" }}>#{i + 1}</span>
                      <span style={{ flex: 1, fontWeight: 600 }}>{t.text}</span>
                      <span className="studio-pill studio-pill--sm">
                        <TrendingUp size={10} /> {Math.round(t.performance_score)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Semantic search */}
            <div className="studio-card" style={{ padding: 18, marginTop: 14 }} data-testid="mem-search-panel">
              <h3><Search size={14} style={{ display: "inline", marginRight: 6 }} />Semantic search</h3>
              <p className="studio-card-sub">
                Try natural-language queries — &quot;hooks about money saving&quot; or &quot;tutorials for beginners&quot;.
              </p>
              <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr auto auto" }}>
                <input
                  className="studio-input"
                  placeholder="Search the memory in plain English…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  data-testid="mem-search-input"
                />
                <select
                  className="studio-input"
                  value={kindFilter}
                  onChange={(e) => setKindFilter(e.target.value)}
                  data-testid="mem-kind-filter"
                >
                  <option value="">All kinds</option>
                  <option value="title">Titles</option>
                  <option value="hook">Hooks</option>
                  <option value="description">Descriptions</option>
                </select>
                <button
                  className="studio-btn-primary"
                  onClick={search}
                  disabled={searching || !query.trim()}
                  data-testid="mem-search-btn"
                >
                  {searching ? <Loader2 size={14} className="ac-spin" /> : <Search size={14} />}
                  Search
                </button>
              </div>

              {results.length > 0 && (
                <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
                  {results.map((r, i) => (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="studio-card"
                      style={{ padding: 12, margin: 0 }}
                      data-testid={`mem-result-${i}`}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span className="studio-pill studio-pill--sm" style={{ textTransform: "uppercase" }}>{r.kind}</span>
                        <span className="studio-pill studio-pill--sm">sim {r.similarity}</span>
                        <span className="studio-pill studio-pill--sm">perf {Math.round(r.performance_score)}</span>
                        <small className="studio-muted" style={{ marginLeft: "auto" }}>score {r.weighted_score}</small>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{r.text}</div>
                    </motion.div>
                  ))}
                </div>
              )}
              {!searching && query && !results.length && (
                <p className="studio-muted" style={{ marginTop: 14 }}>
                  No matches yet. Try clicking <strong>Backfill from content</strong> first.
                </p>
              )}
            </div>
          </>
        )}

        {!loading && !stats && channelId && (
          <div className="studio-empty" data-testid="mem-empty">
            <div className="studio-empty-icon"><Activity size={36} /></div>
            <h2>No memory yet for this channel</h2>
            <p>Click <strong>Backfill from content</strong> to seed it from your existing videos.</p>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
